import { useRef, useState, type FC } from 'react'

import { LoaderCircle, MessagePopup } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { twJoin } from 'tailwind-merge'

import { Translation } from '@/app/translations/Translation'
import { LocalDateTime } from '@/components/LocalDateTime'
import queries from '@/modules/admin/queries'
import adminResources, { type AssetNotificationTimelineItem } from '@/modules/admin/resources'

const PAGE_LIMIT = 5

const notificationTypeLabels: Record<string, string> = {
  asset_return_dunning: 'Reminder SMS to Employee',
  asset_return_confirmation_request: 'Escalation Email to Head of Department',
  asset_return_confirmation_request_sms: 'Escalation SMS to Head of Department',
  asset_return_escalation: 'Final Escalation Email to Escalation Contact',
}

const getNotificationTypeLabel = (notificationType: string) =>
  notificationTypeLabels[notificationType] ?? notificationType

export const NotificationLogPopup: FC<{ assetId: number }> = ({ assetId }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState<AssetNotificationTimelineItem | null>(null)
  const tableContainerRef = useRef<HTMLDivElement | null>(null)
  const tableScrollTopRef = useRef(0)

  const { data: previewData, isLoading: isPreviewLoading } = useQuery(
    queries.adminAssetNotificationTimelinePreview(assetId),
    () => adminResources.getAssetNotificationTimelinePage({ assetId, limit: 1 }),
    {
      enabled: Boolean(assetId),
    }
  )

  const notificationTimelineQuery = useInfiniteQuery({
    queryKey: queries.adminAssetNotificationTimelinePage(assetId),
    queryFn: ({ pageParam = null }) =>
      adminResources.getAssetNotificationTimelinePage({
        assetId,
        limit: PAGE_LIMIT,
        cursor: pageParam as string | null,
      }),
    getNextPageParam: lastPage => lastPage.nextCursor ?? undefined,
    enabled: Boolean(assetId) && isOpen,
  })

  const items = notificationTimelineQuery.data?.pages.flatMap(page => page.items) ?? []

  const openLog = () => {
    setIsOpen(true)
    setSelectedItem(null)
  }

  if (isPreviewLoading || !previewData?.items.length) return null

  return (
    <>
      <Button
        variant="tertiary"
        onClick={openLog}
        className="m-0 border-0 bg-transparent p-0 text-sm underline hover:cursor-pointer"
      >
        <Translation id="See notification log" />
      </Button>

      <MessagePopup
        title={<Translation id="Notification log" />}
        isOpen={isOpen}
        onClose={() => {
          setSelectedItem(null)
          setIsOpen(false)
        }}
        variant="wide"
      >
        {notificationTimelineQuery.isInitialLoading ? (
          <LoaderCircle />
        ) : (
          <div className="relative overflow-hidden">
            <div
              className={twJoin(
                'flex w-[200%] transition-transform duration-300 ease-in-out',
                selectedItem ? '-translate-x-1/2' : 'translate-x-0'
              )}
            >
              <div className="w-1/2 pr-1">
                <div ref={tableContainerRef} className="max-h-96 overflow-y-auto">
                  <table>
                    <thead>
                      <tr>
                        <th>
                          <Translation id="Date and time" />
                        </th>
                        <th>
                          <Translation id="Recipient" />
                        </th>
                        <th>
                          <Translation id="Name" />
                        </th>
                        <th>
                          <Translation id="Type of notification" />
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {items.map(item => (
                        <tr
                          key={item.id}
                          className="cursor-pointer hover:bg-zinc-100"
                          onClick={() => {
                            tableScrollTopRef.current = tableContainerRef.current?.scrollTop || 0
                            setSelectedItem(item)
                          }}
                        >
                          <td>
                            <LocalDateTime date={item.timelineAt} />
                          </td>
                          <td>{item.recipientAddress}</td>
                          <td>{item.recipientName || '-'}</td>
                          <td>{getNotificationTypeLabel(item.notificationType)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="w-1/2 pl-1">
                {selectedItem && (
                  <div className="max-h-96 overflow-y-auto rounded border border-zinc-200 p-4">
                    <div className="mb-2 font-semibold">
                      {getNotificationTypeLabel(selectedItem.notificationType)}
                    </div>

                    <div className="mb-3 grid grid-cols-[170px_1fr] gap-x-3 gap-y-1 text-sm">
                      <div className="font-semibold">
                        <Translation id="Date and time" />
                      </div>
                      <div>
                        <LocalDateTime date={selectedItem.timelineAt} />
                      </div>

                      <div className="font-semibold">
                        <Translation id="Recipient role" />
                      </div>
                      <div>{selectedItem.recipientRole || '-'}</div>

                      <div className="font-semibold">
                        <Translation id="Recipient name" />
                      </div>
                      <div>{selectedItem.recipientName || '-'}</div>

                      <div className="font-semibold">
                        <Translation id="Recipient address" />
                      </div>
                      <div>{selectedItem.recipientAddress}</div>
                    </div>

                    <div className="mb-1 text-sm font-semibold">
                      <Translation id="Subject" />
                    </div>
                    <div className="mb-3 rounded border border-zinc-200 p-2 text-sm">
                      {selectedItem.contentSubject || '-'}
                    </div>

                    <div className="mb-1 text-sm font-semibold">
                      <Translation id="Message text" />
                    </div>
                    <pre className="whitespace-pre-wrap rounded border border-zinc-200 p-2 text-sm">
                      {selectedItem.contentText || selectedItem.messagePreview || '-'}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="mt-3 flex justify-end gap-2">
          {!selectedItem && notificationTimelineQuery.hasNextPage && (
            <Button
              variant="secondary"
              onClick={() => notificationTimelineQuery.fetchNextPage()}
              isLoading={notificationTimelineQuery.isFetchingNextPage}
            >
              <Translation id="Load more" />
            </Button>
          )}
          {selectedItem && (
            <Button
              variant="secondary"
              onClick={() => {
                setSelectedItem(null)
                requestAnimationFrame(() => {
                  if (!tableContainerRef.current) return
                  tableContainerRef.current.scrollTop = tableScrollTopRef.current
                })
              }}
            >
              <Translation id="Back to list" />
            </Button>
          )}
          <Button
            variant="tertiary"
            onClick={() => {
              setSelectedItem(null)
              setIsOpen(false)
            }}
          >
            <Translation id="Close" />
          </Button>
        </div>
      </MessagePopup>
    </>
  )
}
